//@flow
import BaseActions from 'common/redux/actions/BaseActions';
import { injectError } from 'common/redux/actions/error';

import {
    ToastTypes,
    CloseToastButton
} from 'common/components/core/toast/Toast';
import { showToast } from 'common/redux/actions/toast';
import I18nHelper from 'common/helpers/I18nHelper';

import { LocusApiModel, ApiError } from 'common/modules/api';
import type {
    SingleEntityOptionsType,
    ActionTypes,
    FetchEntitiesOptionsType
} from 'common/redux/actions/BaseActions';

import type ImmutableModel from 'common/models/flow/ImmutableModel';
import StringUtils from 'common/utils/StringUtils';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const ONE_MINUTE_IN_MS = 60000;

export default class EntityActions<
    TEntity: ImmutableModel,
    TParams
> extends BaseActions<TEntity, TParams> {
    disableApi: LocusApiModel;
    validateApi: LocusApiModel;
    bulkDisableApi: LocusApiModel;
    entityStatusUpdateApi: LocusApiModel;
    bulkUpdateEntitiesApi: LocusApiModel;

    types: {
        ...ActionTypes,
        DISABLE_ENTITY_START: string,
        DISABLE_ENTITY_SUCCESS: string,
        DISABLE_ENTITY_FAILURE: string
    };

    constructor(
        TAG: string,
        api: LocusApiModel,
        disableApi: LocusApiModel,
        bulkDisableApi: LocusApiModel,
        validateEntityIdsApi: LocusApiModel,
        entityStatusUpdateApi: ?LocusApiModel,
        resetPasswordApi: ?LocusApiModel,
        bulkResetPasswordApi: ?LocusApiModel,
        bulkResetQrCodeApi: ?LocusApiModel,
        bulkUpdateEntitiesApi: ?LocusApiModel
    ) {
        super(TAG, api);
        this.disableApi = disableApi;
        this.bulkDisableApi = bulkDisableApi;
        this.validateApi = validateEntityIdsApi;
        this.types = {
            ...this.types,
            DISABLE_ENTITY_START: 'DISABLE_ENTITY_START',
            DISABLE_ENTITY_SUCCESS: 'DISABLE_ENTITY_SUCCESS',
            DISABLE_ENTITY_FAILURE: 'DISABLE_ENTITY_FAILURE'
        };
        this.entityStatusUpdateApi = entityStatusUpdateApi;
        this.resetPasswordApi = resetPasswordApi;
        this.bulkResetPasswordApi = bulkResetPasswordApi;
        this.bulkResetQrCodeApi = bulkResetQrCodeApi;
        this.bulkUpdateEntitiesApi = bulkUpdateEntitiesApi;
    }

    fetchEntities = (
        params: TParams,
        options: FetchEntitiesOptionsType = {}
    ) => {
        const { processResponse, onSuccess } = options;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitFetchEntitiesStart(params));
            this.api
                .fetch(params)
                .then((response: any) => {
                    let entities = null;
                    if (processResponse) {
                        entities = processResponse(response);
                    } else {
                        entities = (response: Array<Object>);
                    }
                    onSuccess && onSuccess(entities);
                    dispatch(this.injectEntities(params, entities));
                })
                .catch((error: Error) => {
                    dispatch(this.emitFetchEntitiesFailure(params, error));
                    this.handleError(error, options, dispatch);
                });
        };
    };

    validateEntityIdsCall = ({ clientId }, body, options) => {
        const { onSuccess, onFailure } = options;
        const params = { clientId, entityType: this.tag };

        return (dispatch: Dispatch<*>) => {
            this.validateApi
                .post(params, body)
                .then((response: Array<string>) => {
                    onSuccess && onSuccess(response);
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    validateEntityIds = ({ clientId }, body) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const promiseOptions = {
                    onSuccess: response => {
                        resolve(response);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                dispatch(
                    this.validateEntityIdsCall(
                        { clientId },
                        body,
                        promiseOptions
                    )
                );
            });
        };
    };

    disableEntity = (
        params: TParams,
        options: SingleEntityOptionsType = {}
    ) => {
        const {
            processResponse,
            onSuccess,
            onFailure,
            dontInjectError
        } = options;
        const shouldInjectError = !dontInjectError;
        return (dispatch: Dispatch<*>) => {
            dispatch(this.emitDisableEntityStart(params));
            this.disableApi
                .post(params)
                .then((response: any) => {
                    let entity = null;
                    if (processResponse) {
                        entity = processResponse(response);
                    } else {
                        entity = (response: Object);
                    }
                    onSuccess && onSuccess(entity);
                    dispatch(
                        this.injectEntity(
                            params,
                            entity,
                            this.types.DISABLE_ENTITY_SUCCESS
                        )
                    );
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    dispatch(this.emitDisableEntityFailure(params, error));
                    if (error instanceof ApiError) {
                        if (shouldInjectError) {
                            dispatch(injectError(error));
                        }
                        return error;
                    }
                    throw error;
                });
        };
    };

    bulkDisableEntity = ({ params, body, options }) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                this.bulkDisableApi
                    .post(params, body)
                    .then((response: any) => {
                        if (response.status === 'SUCCESS') {
                            let message = 'deleteBulkEntitiesSuccessMessage';
                            if (options.entityCanBeDisabled) {
                                message = 'disableBulkEntitiesSuccessMessage';
                            }
                            const toast = {
                                message: nsTranslate(message, {
                                    entityNameId: this.entityNameId
                                }),
                                type: ToastTypes.Success
                            };
                            dispatch(showToast(toast, params));
                        }
                        resolve(response);
                    })
                    .catch((error: Error) => {
                        reject(error);
                        dispatch(this.emitDisableEntityFailure(params, error));
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                            return error;
                        }
                        throw error;
                    });
            });
        };
    };

    fetchActiveEntities = (
        params: $Shape<TParams>,
        body: Object,
        options: Object
    ) => {
        let filters = [
            { name: 'status', operation: 'EQUALS', value: 'ACTIVE' }
        ];
        if (body.filters) {
            filters = [...filters, ...body.filters];
        }
        return this.fetchEntities(
            params,
            {
                ...body,
                filters
            },
            options
        );
    };

    emitDisableEntityStart(params: TParams) {
        return {
            type: this.types.DISABLE_ENTITY_START,
            params
        };
    }

    emitDisableEntityFailure(params: TParams, error: Error) {
        return {
            type: this.types.DISABLE_ENTITY_FAILURE,
            params,
            error
        };
    }

    entityStatusUpdate = (
        params: TParams,
        options: SingleEntityOptionsType = {},
        body: { status: string }
    ) => {
        const {
            processResponse,
            onSuccess,
            onFailure,
            dontInjectError
        } = options;
        const shouldInjectError = !dontInjectError;
        return (dispatch: Dispatch<*>) => {
            this.entityStatusUpdateApi
                .post(params, body)
                .then((response: any) => {
                    let entity = null;
                    if (processResponse) {
                        entity = processResponse(response);
                    } else {
                        entity = (response: Object);
                    }
                    onSuccess && onSuccess(entity);
                    dispatch(this.injectEntity(params, entity));
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    if (error instanceof ApiError) {
                        if (shouldInjectError) {
                            dispatch(injectError(error));
                        }
                        return error;
                    }
                    throw error;
                });
        };
    };

    resetPassword = (params: TParams): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            const { isReset } = params;
            const resetMessage = isReset
                ? 'resetPasswordSuccessMessage'
                : 'resendPasswordSuccessMessage';
            return this.resetPasswordApi
                .put(params)
                .then((response: any): Object => {
                    const newPasswordMsg = nsTranslate('newPasswordMsg', {
                        password: response.token
                    });
                    const resetMsg = nsTranslate(resetMessage, {
                        entityNameId: this.entityNameId
                    });
                    const toast = {
                        message: `${resetMsg} ${newPasswordMsg}`,
                        type: ToastTypes.Success,
                        timeout: ONE_MINUTE_IN_MS,
                        customAction: CloseToastButton
                    };
                    dispatch(showToast(toast, params));
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return error;
                    }
                    throw error;
                });
        };
    };

    bulkResetPassword = ({
        params,
        body
    }: {
        params: TParams,
        body: Object
    }): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            const { isReset } = params;
            const resetMessage = isReset
                ? 'bulkResetPasswordSuccessMessage'
                : 'bulkResendPasswordSuccessMessage';
            return this.bulkResetPasswordApi
                .post(params, body)
                .then((response: any) => {
                    const toast = {
                        message: StringUtils.toSentenceCaseFromMixedCase(
                            nsTranslate(resetMessage, {
                                entityNameId: this.entityNameId
                            })
                        ),
                        type: ToastTypes.Success,
                        timeout: ONE_MINUTE_IN_MS,
                        customAction: CloseToastButton
                    };
                    dispatch(showToast(toast, params));
                    return response;
                })
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return error;
                    }
                    throw error;
                });
        };
    };

    bulkResetQrCode = ({
        params,
        body
    }: {
        params: TParams,
        body: Object
    }): Function => {
        return (dispatch: Dispatch<*>): Promise<any> => {
            return this.bulkResetQrCodeApi
                .post(params, body)
                .then((response: any) => response)
                .catch((error: Error) => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                        return error;
                    }
                    throw error;
                });
        };
    };

    bulkUpdateEntities = ({
        params,
        value,
        accessor,
        bulkGetRequest
    }: {
        params: TParams,
        value: string,
        accessor: string,
        bulkGetRequest: Object
    }): ((Dispatch<*>) => void) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                // NOTE: Bulk update is not for arrays
                const body = {
                    bulkGetRequest,
                    editsRequest: [
                        {
                            op: 'add',
                            path: `/${accessor.replaceAll('.', '/')}`,
                            value
                        }
                    ],
                    timestamp: new Date().toISOString()
                };
                this.bulkUpdateEntitiesApi
                    .post(params, body)
                    .then((response: any) => {
                        const {
                            summary: { updated, total }
                        } = response;
                        const toast = {
                            message: nsTranslate('bulkEntitiesUpdated', {
                                updated,
                                total
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(response);
                    })
                    .catch((error: Error) => {
                        reject(error);
                        if (error instanceof ApiError) {
                            dispatch(injectError(error));
                            return error;
                        }
                        throw error;
                    });
            });
        };
    };
}
